import * as React from 'react';

import { cn } from '../utils/cn';

type SelectValueType = string | undefined;

interface SelectItemConfig {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectContextValue {
  value: SelectValueType;
  setValue: (nextValue: string) => void;
  items: SelectItemConfig[];
  setItems: React.Dispatch<React.SetStateAction<SelectItemConfig[]>>;
  placeholder?: string;
  setPlaceholder: React.Dispatch<React.SetStateAction<string | undefined>>;
  disabled?: boolean;
}

const SelectContext = React.createContext<SelectContextValue | null>(null);

function useSelectContext(component: string): SelectContextValue {
  const context = React.useContext(SelectContext);
  if (!context) {
    throw new Error(`${component} must be used within a Select`);
  }
  return context;
}

export interface SelectProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  children: React.ReactNode;
}

export const Select = ({ value, defaultValue, onValueChange, disabled, children }: SelectProps) => {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = React.useState<string | undefined>(defaultValue);
  const [items, setItems] = React.useState<SelectItemConfig[]>([]);
  const [placeholder, setPlaceholder] = React.useState<string | undefined>();

  const currentValue = isControlled ? value : internalValue;

  const setValue = React.useCallback(
    (nextValue: string) => {
      if (!isControlled) {
        setInternalValue(nextValue);
      }
      onValueChange?.(nextValue);
    },
    [isControlled, onValueChange]
  );

  const contextValue = React.useMemo<SelectContextValue>(
    () => ({
      value: currentValue,
      setValue,
      items,
      setItems,
      placeholder,
      setPlaceholder,
      disabled
    }),
    [currentValue, disabled, items, placeholder, setValue]
  );

  return <SelectContext.Provider value={contextValue}>{children}</SelectContext.Provider>;
};

Select.displayName = 'Select';

type SelectTriggerProps = Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'defaultValue'>;

export const SelectTrigger = React.forwardRef<HTMLSelectElement, SelectTriggerProps>(
  ({ className, children: _children, disabled, onChange, ...props }, ref) => {
    const { value, setValue, items, placeholder, disabled: contextDisabled } = useSelectContext('SelectTrigger');

    const handleChange = React.useCallback(
      (event: React.ChangeEvent<HTMLSelectElement>) => {
        const nextValue = event.target.value;
        setValue(nextValue);
        onChange?.(event);
      },
      [onChange, setValue]
    );

    const hasPlaceholder = placeholder !== undefined && placeholder !== null;
    const hasValue = value !== undefined && value !== '';

    return (
      <select
        ref={ref}
        value={value ?? ''}
        onChange={handleChange}
        disabled={contextDisabled || disabled}
        className={cn(
          'flex h-10 w-full appearance-none rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        {...props}
      >
        {hasPlaceholder ? (
          <option value="" disabled={hasValue} hidden={hasValue}>
            {placeholder}
          </option>
        ) : null}
        {items.map(item => (
          <option key={item.value} value={item.value} disabled={item.disabled}>
            {item.label}
          </option>
        ))}
      </select>
    );
  }
);

SelectTrigger.displayName = 'SelectTrigger';

export interface SelectContentProps {
  children: React.ReactNode;
}

export interface SelectItemProps {
  value: string;
  children: React.ReactNode;
  disabled?: boolean;
}

function itemsAreEqual(a: SelectItemConfig[], b: SelectItemConfig[]): boolean {
  if (a === b) {
    return true;
  }
  if (a.length !== b.length) {
    return false;
  }
  return a.every((left, index) => {
    const right = b[index];
    return left.value === right.value && left.disabled === right.disabled && left.label === right.label;
  });
}

function childrenToText(children: React.ReactNode): string {
  const collectText = (node: React.ReactNode): string[] => {
    if (node === null || node === undefined || typeof node === 'boolean') {
      return [];
    }
    if (typeof node === 'string' || typeof node === 'number') {
      return [String(node)];
    }
    if (Array.isArray(node)) {
      return node.flatMap(child => collectText(child));
    }
    if (React.isValidElement(node)) {
      return collectText((node.props as { children?: React.ReactNode }).children);
    }
    return [];
  };

  return collectText(children)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export const SelectContent = ({ children }: SelectContentProps) => {
  const { setItems } = useSelectContext('SelectContent');

  const parsedItems = React.useMemo(() => {
    return React.Children.toArray(children)
      .map(child => {
        if (!React.isValidElement(child) || child.type !== SelectItem) {
          return null;
        }
        const props = child.props as SelectItemProps;
        const label = childrenToText(props.children);
        return {
          value: props.value,
          disabled: props.disabled,
          label: label || props.value
        } satisfies SelectItemConfig;
      })
      .filter((item): item is SelectItemConfig => item !== null);
  }, [children]);

  React.useEffect(() => {
    setItems(previous => (itemsAreEqual(previous, parsedItems) ? previous : parsedItems));
  }, [parsedItems, setItems]);

  return null;
};

SelectContent.displayName = 'SelectContent';

export const SelectItem = ({ children: _children }: SelectItemProps) => null;

SelectItem.displayName = 'SelectItem';

export interface SelectValueProps {
  placeholder?: string;
}

export const SelectValue = ({ placeholder }: SelectValueProps) => {
  const { setPlaceholder } = useSelectContext('SelectValue');

  React.useEffect(() => {
    setPlaceholder(placeholder);
    return () => setPlaceholder(undefined);
  }, [placeholder, setPlaceholder]);

  return null;
};

SelectValue.displayName = 'SelectValue';
